// Revival Overview — every quote as one table at the Design Standard.
// One-row header (one primary: Scan a ticket), a readable stat strip, one row of
// filter chips, and the table: quote no., customer, address, date on ticket, total,
// status, source. A row opens the quote in the movable window. On the phone the
// table becomes a list. Behind REVIEW_QUEUE_ON (off) the chips become
// All · Needs review · Completed · Not completed and Needs review is answered in
// place (ReviewQueue + CustomerMatches).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useIsPhone from "../../hooks/useIsPhone";
import { DataTable } from "../ui/table";
import Badge from "../ui/badge";
import Button from "../ui/button";
import RevivalHeader, { StatStrip } from "./RevivalHeader";
import revivalApi from "../../api/revivalApi";
import QuoteWindow from "./QuoteWindow";
import ReviewQueue from "./ReviewQueue";
import CustomerMatches from "./CustomerMatches";
import { createRevivalCampaignDraft } from "./campaignDrafts";
import { REVIEW_QUEUE_ON } from "./revivalFlags";
import {
  addressOf, count, money, moneyWhole, quoteTotal, shortDate, sourceLabel, statusLabel, statusTone,
  ticketDateOf, useRevivalQuotes, useScanSummary,
} from "./revivalData";
import "./revival.css";

const STATUS_ORDER = ["draft", "sent", "viewed", "accepted", "converted", "active", "completed", "rejected", "renewal_due"];

const matches = (q, term) => {
  if (!term) return true;
  const hay = [q.quote_number, q.customer_name, q.address, q.city, q.customer_phone].join(" ").toLowerCase();
  return hay.includes(term);
};

export default function RevivalOverview() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { rows, loading, error, replace, reload } = useRevivalQuotes();
  const { summary } = useScanSummary();
  const [chip, setChip] = useState("all");
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState([]);
  const [campaigning, setCampaigning] = useState(false);
  const [campaignError, setCampaignError] = useState("");

  // The queue needs leg 19's work_status on the rows; without it the flag stays inert.
  const reviewOn = REVIEW_QUEUE_ON && rows.some((q) => "work_status" in q);

  // ?quote_id= deep link (campaign + notification links) opens the window.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("quote_id");
    if (!id || !rows.length) return;
    const hit = rows.find((q) => String(q.id) === String(id));
    if (hit) setOpen(hit);
  }, [rows]);

  const chips = useMemo(() => {
    if (reviewOn) {
      const scanned = rows.filter((q) => q.source === "scanner");
      return [
        ["all", "All", rows.length],
        ["review", "Needs review", scanned.filter((q) => !q.work_status).length],
        ["work:completed", "Completed", rows.filter((q) => q.work_status === "completed").length],
        ["work:not_completed", "Not completed", rows.filter((q) => q.work_status === "not_completed").length],
      ];
    }
    const by = rows.reduce((m, q) => { const s = q.status || "draft"; m[s] = (m[s] || 0) + 1; return m; }, {});
    const known = STATUS_ORDER.filter((s) => by[s]);
    const other = Object.keys(by).filter((s) => !STATUS_ORDER.includes(s));
    return [["all", "All", rows.length], ...[...known, ...other].map((s) => [`status:${s}`, statusLabel(s), by[s]])];
  }, [rows, reviewOn]);

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase();
    return rows.filter((q) => {
      if (!matches(q, t)) return false;
      if (chip === "all") return true;
      if (chip === "review") return q.source === "scanner" && !q.work_status;
      if (chip.startsWith("work:")) return q.work_status === chip.slice(5);
      if (chip.startsWith("status:")) return (q.status || "draft") === chip.slice(7);
      return true;
    });
  }, [rows, chip, term]);

  const totalValue = visible.reduce((s, q) => s + (quoteTotal(q) || 0), 0);
  const selectedQuotes = rows.filter((q) => selected.includes(q.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const startCampaignDraft = async () => {
    if (!selectedQuotes.length) return;
    setCampaigning(true);
    setCampaignError("");
    try {
      const { data } = await createRevivalCampaignDraft({
        quotes: selectedQuotes,
        name: selectedQuotes.length === 1 ? `Quote Follow-up - ${selectedQuotes[0].customer_name || "Customer"}` : "",
      });
      navigate("/revival/campaigns", { state: { campaignId: data?.id, createdCampaignId: data?.id, seedQuotes: selectedQuotes } });
    } catch (err) {
      setCampaignError(`Couldn't create the campaign draft: ${err?.message || "unknown error"}`);
    } finally {
      setCampaigning(false);
    }
  };

  const allOn = visible.length > 0 && visible.every((q) => selected.includes(q.id));
  const columns = [
    {
      key: "_sel", sort: false,
      label: <input type="checkbox" className="rv-check" aria-label="Select all shown" checked={allOn}
        onChange={() => setSelected(allOn ? [] : visible.map((q) => q.id))} />,
      render: (q) => (
        <input type="checkbox" className="rv-check" aria-label={`Select ${q.quote_number || q.customer_name || "quote"}`}
          checked={selected.includes(q.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(q.id)} />
      ),
    },
    { key: "quote_number", label: "Quote", render: (q) => <span className="rv-cell-dim">{q.quote_number || "—"}</span> },
    { key: "customer_name", label: "Customer", sortValue: (q) => q.customer_name || null, render: (q) => <span className="rv-cell-name">{q.customer_name || "—"}</span> },
    { key: "address", label: "Address", sortValue: (q) => addressOf(q) || null, render: (q) => <span className="rv-cell-clip rv-cell-dim" title={addressOf(q)}>{addressOf(q) || "—"}</span> },
    { key: "ticket", label: "Date on ticket", sortValue: (q) => ticketDateOf(q) || null, render: (q) => shortDate(ticketDateOf(q)) },
    { key: "total", label: "Total", num: true, sortValue: (q) => quoteTotal(q), render: (q) => money(quoteTotal(q)) },
    { key: "status", label: "Status", render: (q) => <Badge tone={statusTone(q.status || "draft")}>{statusLabel(q.status || "draft")}</Badge> },
    { key: "source", label: "Source", render: (q) => <span className="rv-cell-dim">{sourceLabel(q.source)}</span> },
  ];

  let body;
  // Only the first load blanks the list: a reload after a save keeps the queue
  // (and its answered rows) mounted.
  if (loading && !rows.length) body = <div className="rv-loading">Loading quotes…</div>;
  else if (chip === "review") {
    body = (
      <>
        {/* OPS's tag has no list of the different-address quotes yet: no list, no section. */}
        {revivalApi.listCustomerMatches ? <CustomerMatches load={revivalApi.listCustomerMatches} onSaved={reload} /> : null}
        <ReviewQueue quotes={visible} onOpen={setOpen} onSaved={(id, patch) => replace(id, patch)} />
      </>
    );
  } else if (isPhone) {
    body = (
      <div className="rv-list">
        {visible.map((q) => (
          <button key={q.id} type="button" className="rv-row" onClick={() => setOpen(q)}>
            <span className="rv-row-main">
              <b>{q.customer_name || "—"}</b>
              <span>{[q.quote_number, shortDate(ticketDateOf(q)), sourceLabel(q.source)].filter((s) => s && s !== "—").join(" · ")}</span>
            </span>
            <span className="rv-row-end">
              <b>{money(quoteTotal(q))}</b>
              <span>{statusLabel(q.status || "draft")}</span>
            </span>
          </button>
        ))}
        {!visible.length ? <div className="rv-loading">No quotes match this filter.</div> : null}
      </div>
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={visible}
        onRowClick={setOpen}
        maxHeight="calc(100vh - 260px)"
        empty={rows.length ? "No quotes match this filter." : "No quotes yet. Scan a ticket and it lands here."}
      />
    );
  }

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="Quotes"
        totals={loading ? [] : [`${count(visible.length)} quotes`, money(totalValue)]}
        menu={[
          { label: "Create a quote", onClick: () => navigate("/quotes/create") },
          { label: "Campaigns", onClick: () => navigate("/revival/campaigns") },
        ]}
        primary={<Button variant="primary" size="sm" onClick={() => navigate("/revival/scanner")}>Scan a ticket</Button>}
      />

      <StatStrip
        stats={[
          { label: "Scanned value", value: moneyWhole(summary?.total_scanned_value), note: summary ? `${count(summary.total_scanned)} scanned quotes` : null },
          { label: "Collected", value: moneyWhole(summary?.total_collected) },
          { label: "Unpaid remaining", value: moneyWhole(summary?.unpaid_remaining) },
          { label: "Partially paid", value: count(summary?.partially_paid_quotes) },
        ]}
      />

      <div className="rv-toolbar">
        <div className="rv-chips" role="toolbar" aria-label="Filter quotes">
          {chips.map(([k, l, n]) => (
            <button key={k} type="button" className={`rv-chip${chip === k ? " is-active" : ""}`} aria-pressed={chip === k} onClick={() => setChip(k)}>
              {l} <b>{n}</b>
            </button>
          ))}
        </div>
        <input className="rv-search" type="search" placeholder="Search quotes" aria-label="Search quotes" value={term} onChange={(e) => setTerm(e.target.value)} />
      </div>

      {selected.length ? (
        <div className="rv-selbar">
          <span>{selected.length} selected</span>
          <Button size="sm" onClick={startCampaignDraft} loading={campaigning}>Create campaign draft</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
          {campaignError ? <span className="rv-qstate is-bad" role="alert">{campaignError}</span> : null}
        </div>
      ) : null}

      {error ? <div className="rv-error" role="alert">{error}</div> : null}
      {body}

      {open ? (
        <QuoteWindow
          quote={open}
          onClose={() => setOpen(null)}
          onDeleted={(id) => { setSelected((s) => s.filter((x) => x !== id)); reload(); }}
        />
      ) : null}
    </div>
  );
}
